import _ from 'lodash';
import { Fragment, useState, useCallback } from 'react';

import {
  Tab,
  List,
  Tabs,
  Stack,
  Button,
  Dialog,
  Divider,
  ListItem,
  Pagination,
  Typography,
  DialogTitle,
  ListItemText,
  DialogActions,
  DialogContent,
} from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { fDateTime } from 'src/utils/format-time';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { PageOptions } from 'src/services/api.types';
import { useGetApplicationInterviewsQuery } from 'src/services/interviews/interviews.service';
import {
  InterviewStatus,
  ScheduledInterviewResponse,
} from 'src/services/interviews/interview.types';
import { openInterviewActionModal, openInterviewSchedulerModal } from 'src/store/slices/interviews/interviewSlice';

import Label from 'src/components/label';
import EmptyContent from 'src/components/empty-content';

import { TenantType } from 'src/types/enums';

type Props = {
  open: boolean;
  applicationId: string;
  onClose: VoidFunction;
};

const TAB_OPTIONS = ['pending', 'scheduled', 'completed'];

export default function InterviewListModal({ open, applicationId, onClose }: Props) {
  const { t } = useTranslate();

  const tenantType = useOrgTenant();

  const dispatch = useAppDispatch();

  const [selectedTab, setSelectedTab] = useState<string>('pending');

  const [pageOptions, setPageOptions] = useState<PageOptions>({
    page: 1,
    per_page: 5,
  });

  const { currentData: interviews } = useGetApplicationInterviewsQuery({
    applicationId,
    params: {
      ...pageOptions,
      status: selectedTab as "pending" | "completed" | "scheduled"
    }
  });

  const onChangePage = useCallback((value: number) => {
    setPageOptions((prev) => ({
      ...prev,
      page: value,
    }));
  }, []);

  const onSelectTab = (_event: any, tab: 'active' | 'all') => {
    setSelectedTab(tab);
  }

  const getStatusLabelText = (interview: ScheduledInterviewResponse) => {
    switch (interview.status) {
      case InterviewStatus.Sent:
        return t(
          `enums.interview_status.sent_${tenantType === TenantType.Candidate ? 'candidate' : 'org'}`
        );
      case InterviewStatus.ChangesRequested:
        return t('enums.interview_status.changes_requested');
      case InterviewStatus.RescheduleRequested:
        return t('enums.interview_status.reschedule_requested');
      case InterviewStatus.Scheduled:
        return t('enums.interview_status.scheduled');
      case InterviewStatus.Cancelled:
        return t('enums.interview_status.cancelled');
      case InterviewStatus.Completed:
        return t('enums.interview_status.completed');
      case InterviewStatus.InReview:
        return tenantType === TenantType.Client
          ? t('enums.interview_status.in_review_client')
          : t('enums.interview_status.in_review_agency');
      default:
        return '';
    }
  };

  const getSubtitle = (interview: ScheduledInterviewResponse) => {
    if (interview.status === InterviewStatus.Scheduled) {
      return fDateTime(interview.scheduled_at);
    }

    if (interview.status === InterviewStatus.Cancelled) {
      return t('common.cancelled');
    }

    return t('applications.interview_list_modal.not_scheduled');
  };

  const renderActionButton = (interview: ScheduledInterviewResponse) => {
    if (interview.status === InterviewStatus.InReview && tenantType === TenantType.Recruiter) {
      return (
        <Button
          variant="outlined"
          color="primary"
          onClick={() => dispatch(openInterviewActionModal({ interviewId: interview.id }))}
        >
          {t('common.review')}
        </Button>
      );
    }

    if (interview.status === InterviewStatus.Sent && tenantType !== TenantType.Client) {
      return (
        <Button
          variant="outlined"
          color="primary"
          size='small'
          onClick={() => dispatch(openInterviewActionModal({ interviewId: interview.id }))}
        >
          {t('common.view')}
        </Button>
      );
    }


    if (tenantType === TenantType.Client) {
      return (
        <Button
          variant="outlined"
          color="primary"
          size='small'
          onClick={() => {
            dispatch(
              openInterviewSchedulerModal({
                applicationId,
                interviewId: interview.id,
              })
            );
          }}
        >
          {t('common.view')}
        </Button>
      );
    }

    return null;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('applications.interview_list_modal.title')}</DialogTitle>
      <DialogContent>
        <Tabs
          value={selectedTab}
          onChange={onSelectTab}
          sx={{
            px: 2,
            pb: 2,
            mt: -2
          }}
        >
          {TAB_OPTIONS.map((tab) => (
            <Tab
              key={tab}
              iconPosition="end"
              value={tab}
              label={
                <Typography
                  variant="subtitle2"
                  sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                >
                  {t(`applications.interview_list_modal.tabs.${tab}`)}
                </Typography>
              }
            />
          ))}
        </Tabs>

        <Stack
          direction="column"
          sx={{ minHeight: 400, height: '100%', typography: 'body1' }}
          justifyContent="space-between"
        >
          {interviews?.count === 0 ? (
            <EmptyContent
              filled
              title={t('applications.interview_list_modal.no_interviews')}
              sx={{
                py: 10,
              }}
            />
          ) : (
            <List sx={{ paddingBottom: 2, maxWidth: '100%' }}>
              {interviews?.results.map((interview) => (
                <Fragment key={interview.id}>
                  <ListItem
                    secondaryAction={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Label color="info">{getStatusLabelText(interview)}</Label>
                        {renderActionButton(interview)}
                      </Stack>
                    }
                  >
                    <ListItemText primary={_.capitalize(interview.title)} secondary={getSubtitle(interview)} secondaryTypographyProps={{ variant: 'caption' }} />
                  </ListItem>
                  <Divider />
                </Fragment>
              ))}
            </List>
          )}

          <Stack flexDirection="row" justifyContent="center" sx={{ py: 2 }}>
            <Pagination
              page={pageOptions.page || 1}
              count={Math.ceil((interviews?.count || 1) / pageOptions.per_page)}
              onChange={(_e, value) => onChangePage(value)}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
